import { ApiClient } from '@/shared/api-client';
import { RedirectHelper } from '@/shared/api-client/helpers/redirect-helper';
import { ArgumentValidator } from '@/shared/helpers/argument-validator';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { UIEventHandler } from '@/shared/helpers/ui-event-handler';
import { IUserTask } from '@/shared/api-client/models/user-task/user-task';
import UserTaskBadgeComponent from '@/public/overview/components/user-task-badge-component/user-task-badge-component.vue';
import UserTaskIconComponent from '@/public/overview/components/user-task-icon-component/user-task-icon-component.vue';
import { UserTaskStage, UserTaskStageHelper } from '@/public/shared/helpers/user-task-stage-helper';
import { IUserTaskListRequest } from '@/shared/api-client/models/user-task/user-task-list-request';
import { StringComparer } from '@/shared/helpers/string-comparer';
import { UserTaskEntityType } from '@/shared/api-client/enums/user-task-entity-type';

interface IViewModel {
    isInitialized: boolean;
    userTasks?: IUserTask[];
    hasUserTasks: boolean;
    view: 'Table' | 'Grid'
}

type FilterType = 'entityType' | 'stage';
interface IFilters {
    entityType: UserTaskEntityType[];
    stage: UserTaskStage[];
}
type FilterValues<T extends FilterType> = IFilters[T][number];

@Component({
    components: {
        UserTaskIconComponent,
        UserTaskBadgeComponent
    },
    computed: {
        StringComparer() {
            return StringComparer
        }
    }
})
export default class UserTaskListComponent extends Vue {

    @Prop({ default: 'sv-SE' }) public cultureAlias!: string;
    @Prop() public translations!: { [key: string]: string; };
    @Prop() public staleElectionId!: string;

    public viewModel: IViewModel = {
        isInitialized: false,
        hasUserTasks: false,
        view: 'Table'
    };

    private filters: IFilters = {
        entityType: ['Election', 'ActivityMeeting', 'ActivityMotion'],
        stage: ['Preparation', 'InProgress', 'Finished']
    }

    private apiClient: ApiClient = new ApiClient();

    public get userTasks(): IUserTask[] {

        let tasks = (this.viewModel.userTasks || []);

        tasks = tasks.filter(x => this.filters.stage.includes(UserTaskStageHelper.getStage(x)));

        tasks = tasks.filter(x => this.filters.entityType.includes(x.entityType));

        return tasks;
    }

    public get hasUserTasks(): boolean {
        return this.userTasks.length > 0;
    }

    public hasFilter<T extends FilterType>(filterType: T, filterName: FilterValues<T>): boolean {
        const filterList = this.filters[filterType] as FilterValues<T>[];
        return filterList.includes(filterName);
    }

    public toggleFilter<T extends FilterType>(filterType: T, filterName: FilterValues<T>): void {
        const filterList = this.filters[filterType] as FilterValues<T>[];

        if (filterList.includes(filterName)) {
            this.filters[filterType] = filterList.filter(x => x !== filterName) as IFilters[T];
        } else {
            this.filters[filterType] = [...filterList, filterName] as IFilters[T];
        }
    }

    public get userTaskIconTranslations(): { [key: string]: string; } {

        return {
            'Election': this.translations['icon_label_election'],
            'ActivityMeeting': this.translations['icon_label_activityMeeting'],
            'ActivityMotion': this.translations['icon_label_activityMotioning']
        };
    }

    public async created() {

        await this.loadActivities();

        this.viewModel.isInitialized = true;
    }

    private async loadActivities(): Promise<void> {

        const request: IUserTaskListRequest = {};

        request.cultureAlias = this.cultureAlias;

        if (!StringComparer.isNullOrEmpty(this.staleElectionId)) {
            request.staleEntityType = 'Election';
            request.staleEntityId = this.staleElectionId;
        }

        const tasks = await this.apiClient.userTask.list(request);

        this.viewModel.userTasks = tasks;
        this.viewModel.hasUserTasks = tasks.length > 0;
    }

    public clickVote(userTask: IUserTask, $event?: UIEvent): void {

        ArgumentValidator.notNullOrEmpty(userTask, `userTask`);
        UIEventHandler.preventDefaults($event);

        if (!this.canVote(userTask)) {
            return;
        }

        RedirectHelper.redirectTo(`/vote/index/elections/${encodeURI(userTask.entityId)}`);
    }

    public canVote(userTask: IUserTask): boolean {

        ArgumentValidator.notNullOrEmpty(userTask, `userTask`);

        if (userTask.entityType !== 'Election') {
            return false;
        }

        if (userTask.propertyBag.electionState !== 'ElectionVoting') {
            return false;
        }

        if (userTask.propertyBag.electionHasVoted === 'Yes') {
            return false;
        }

        return true;
    }

    public clickNominate(userTask: IUserTask, $event?: UIEvent): void {

        ArgumentValidator.notNullOrEmpty(userTask, `userTask`);
        UIEventHandler.preventDefaults($event);

        if (!this.canNominate(userTask)) {
            return;
        }

        RedirectHelper.redirectTo(`/nomination/index/elections/${encodeURI(userTask.entityId)}`);
    }

    public canNominate(userTask: IUserTask): boolean {

        ArgumentValidator.notNullOrEmpty(userTask, `userTask`);

        if (userTask.entityType !== 'Election') {
            return false;
        }

        if (userTask.propertyBag.electionState !== 'ElectionNomination') {
            return false;
        }

        return true;
    }

    public showElection(userTask: IUserTask, $event?: UIEvent): void {

        ArgumentValidator.notNullOrEmpty(userTask, `userTask`);
        UIEventHandler.preventDefaults($event);

        if(!this.canShowElection(userTask)) {
            return;
        }

        RedirectHelper.redirectTo(`/election/show/elections/${encodeURI(userTask.entityId)}`);
    }

    public canShowElection(userTask: IUserTask): boolean {

        ArgumentValidator.notNullOrEmpty(userTask, `userTask`);

        if (userTask.entityType !== 'Election') {
            return false;
        }

        return true;
    }

    public clicked(userTask: IUserTask, $event?: UIEvent): void {

        ArgumentValidator.notNullOrEmpty(userTask, `userTask`);
        UIEventHandler.preventDefaults($event);

        switch (userTask.entityType) {
            case 'ActivityMeeting':
                RedirectHelper.redirectTo(`/activity/meeting/${encodeURI(userTask.entityId)}`);
                break;

            case 'ActivityMotion':
                RedirectHelper.redirectTo(`/activity/motioning/${encodeURI(userTask.entityId)}`);
                break;

            case 'Election':

                switch (userTask.propertyBag.electionState) {

                    case 'ElectionNomination':
                        this.clickNominate(userTask);
                        break;

                    case 'ElectionVoting':
                        if (this.canVote(userTask)) {
                            this.clickVote(userTask);
                        } else {
                            this.showElection(userTask);    
                        }
                        break;

                    case 'ElectionPreparation':
                    case 'ElectionFinished':
                        this.showElection(userTask);
                        break;
                }

                break;
                
            default:
                throw new Error(`Not implemented type`);
        }
        
    }
}
